import { AlarmClock, ArrowLeft, ArrowRight, Compass, Lightbulb, PartyPopper, SearchX, Target } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EXAM_BY_ID, PASS_SCORE } from "../data/exams";
import { QUESTION_BY_ID } from "../data/questions";
import { QuestionView } from "../components/QuestionView";
import { Empty, ProgressBar, ScoreRing, useDocumentTitle } from "../components/ui";
import { formatDuration } from "../lib/dates";
import { isCorrect } from "../lib/scoring";
import { buildReview, displayTitle, domainLabel, sessionStore, stableOptionOrder } from "../lib/session";
import { useProgress } from "../lib/store";
import { placementFromAttempt } from "../lib/diagnostic";
import { pick, useT } from "../i18n";

export function ResultPage() {
  const { attemptId } = useParams();
  const p = useProgress();
  const navigate = useNavigate();
  const [onlyWrong, setOnlyWrong] = useState(false);
  const t = useT();
  const lang = p.lang;
  const a = p.attempts.find((x) => x.id === attemptId);
  const title = a ? displayTitle(a, lang) : "";
  useDocumentTitle(a ? `${t("Kết quả", "Results")}: ${title}` : t("Kết quả", "Results"));

  if (!a) {
    return (
      <Empty icon={SearchX} title={t("Không tìm thấy kết quả", "Result not found")}>
        <Link to="/progress">{t("Xem lịch sử", "View history")}</Link>
      </Empty>
    );
  }

  const exam = a.examId ? EXAM_BY_ID[a.examId] : undefined;
  const wrongIds = a.questionIds.filter((id) => {
    const q = QUESTION_BY_ID.get(id);
    return q && !isCorrect(a.answers[id] ?? [], q.answer);
  });
  const isMock = a.kind === "mock";
  const pct = a.total ? (a.correct / a.total) * 100 : 0;
  const domains = Object.entries(a.byDomain)
    .map(([key, tally]) => ({ id: key, name: domainLabel(key), ...tally }))
    .sort((x, y) => x.correct / x.total - y.correct / y.total);
  const weakest = domains[0] && domains[0].correct < domains[0].total ? domains[0] : undefined;

  const retryWrong = () => {
    const s = buildReview(wrongIds, { vi: "Làm lại câu sai", en: "Retry missed questions" });
    if (!s) return;
    sessionStore.set(s);
    navigate("/session");
  };

  const shown = onlyWrong ? wrongIds : a.questionIds;
  const placement = a.kind === "diagnostic" ? placementFromAttempt(a) : undefined;

  return (
    <>
      <p className="small">{exam ? <Link to={`/exams/${exam.id}`}><ArrowLeft className="ico" size={16} aria-hidden="true" /> {exam.code}</Link> : <Link to="/"><ArrowLeft className="ico" size={16} aria-hidden="true" /> {t("Tổng quan", "Home")}</Link>}</p>
      <h1>{title}</h1>
      {a.timedOut && <div className="callout warn"><AlarmClock className="ico" size={16} aria-hidden="true" /> {t("Hết giờ — bài đã được tự động nộp.", "Time's up — your exam was submitted automatically.")}</div>}

      <div className="card row" style={{ gap: "1.5rem" }}>
        <ScoreRing percent={isMock ? (a.scaled / 1000) * 100 : pct} color={isMock ? (a.passed ? "var(--ok)" : "var(--bad)") : undefined}>
          <div>
            <div className="big">{isMock ? a.scaled : `${Math.round(pct)}%`}</div>
            <div className="tiny muted">{isMock ? "/ 1000" : t("chính xác", "accuracy")}</div>
          </div>
        </ScoreRing>
        <div style={{ flex: 1, minWidth: 220 }}>
          {isMock && (
            <p>
              <span className={`chip ${a.passed ? "ok" : "bad"}`} style={{ fontSize: "1rem" }}>
                {a.passed ? <><PartyPopper className="ico" size={18} aria-hidden="true" /> {t("ĐẠT", "PASS")}</> : t("Chưa đạt", "Not passed")}
              </span>{" "}
              <span className="small muted">{t(`ngưỡng ${PASS_SCORE}/1000 (quy đổi tuyến tính, mang tính ước lượng)`, `pass mark ${PASS_SCORE}/1000 (linear estimate)`)}</span>
            </p>
          )}
          <p className="mb-0">{t("Đúng", "Correct")} <strong>{a.correct}/{a.total}</strong> · {t("thời gian", "time")} {formatDuration(a.finishedAt - a.startedAt)}</p>
          {weakest && (
            <p className="small mt mb-0">
              <Lightbulb className="ico" size={16} aria-hidden="true" /> {t("Điểm yếu nhất", "Weakest area")}: <strong>{weakest.name}</strong> ({weakest.correct}/{weakest.total}). {t("Hãy ôn bài học và luyện thêm domain này.", "Review the lessons and practice this domain more.")}
            </p>
          )}
          <div className="row mt">
            {wrongIds.length > 0 && <button type="button" className="btn primary" onClick={retryWrong}>{t(`Làm lại ${wrongIds.length} câu sai`, `Retry ${wrongIds.length} missed`)}</button>}
            {exam && <Link className="btn" to={`/exams/${exam.id}`}>{t("Về trang đề", "Back to exam")}</Link>}
          </div>
        </div>
      </div>

      {placement && (
        <div className="callout ok mt">
          <h2 className="row-inline"><Compass className="ico" size={22} aria-hidden="true" /> {pick(placement.headline, lang)}</h2>
          <p>{pick(placement.advice, lang)}</p>
          <p className="small">
            {placement.perLevel.map((l) => `${EXAM_BY_ID[l.exam].code} ${l.correct}/${l.total}`).join(" · ")}
          </p>
          <Link className="btn primary" to={`/learn/${placement.firstLessonId}`}>{t("Bắt đầu học", "Start learning")} <ArrowRight className="ico" size={16} aria-hidden="true" /></Link>
        </div>
      )}

      {domains.length > 1 && !placement && (
        <>
          <div className="section-title"><h2>{t("Theo domain", "By domain")}</h2></div>
          <div className="card">
            {domains.map((d) => (
              <div key={d.id} className="domain-row">
                <div style={{ fontWeight: 600 }}>{d.name}</div>
                <ProgressBar value={(d.correct / d.total) * 100} label={d.name} tone={d.correct / d.total >= 0.72 ? "ok" : undefined} />
                <span className="small">{d.correct}/{d.total}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section-title">
        <h2>{t("Xem lại đáp án", "Review answers")}</h2>
        <div className="pill-group">
          <button type="button" className="pill" aria-pressed={!onlyWrong} onClick={() => setOnlyWrong(false)}>{t("Tất cả", "All")} ({a.total})</button>
          <button type="button" className="pill" aria-pressed={onlyWrong} onClick={() => setOnlyWrong(true)}>{t("Câu sai", "Missed")} ({wrongIds.length})</button>
        </div>
      </div>
      <div className="stack">
        {shown.map((id) => {
          const q = QUESTION_BY_ID.get(id);
          if (!q) return null;
          return (
            <div key={id} className="card">
              <QuestionView
                question={q}
                order={stableOptionOrder(q)}
                selected={a.answers[id] ?? []}
                revealed
                number={a.questionIds.indexOf(id) + 1}
              />
            </div>
          );
        })}
        {!shown.length && <Empty icon={Target} title={t("Không có câu sai nào!", "No missed questions!")} />}
      </div>
    </>
  );
}
